/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 *
 * Design System — DataTable pinned (frozen) columns.
 *
 * A pinned column stays against the table's edge while the body scrolls
 * horizontally. The overwhelmingly common case is a trailing actions column that
 * must remain reachable on a wide table; the identity column and the selection
 * checkboxes are the usual left-hand pair.
 *
 * Implemented with `position: sticky` plus an offset per cell, which means each
 * pinned column needs a known width — a column that flexed would give the
 * columns beside it nothing fixed to sit against. Widths are resolved from the
 * same source the layout uses, so dragging a pinned column keeps the offsets
 * correct.
 */
import { useMemo } from 'react';

import type { DataTableColumn, DataTablePinned } from '../../types';
import type { ColumnWidths } from './useColumnWidths';

/** Stands in for the selection column, which has no caller-supplied key. */
export const SELECTION_COLUMN_KEY = '__ds-dt-selection__';

export interface PinnedOffset {
  side: DataTablePinned;
  /** Distance from the pinned edge, in px. */
  offset: number;
  /**
   * Whether this is the innermost pinned column on its side — the one that
   * carries the divider separating pinned from scrolling content.
   */
  isEdge: boolean;
}

export type PinnedOffsets = Record<string, PinnedOffset>;

interface ComputePinnedParams<TData> {
  columns: DataTableColumn<TData>[];
  /** User-dragged widths, which win over a column's declared width. */
  widths: ColumnWidths;
  hasSelection: boolean;
  selectionWidth: number;
}

/**
 * Warn once per column. A table re-rendering on a poll would otherwise repeat
 * the same message on every tick.
 */
const warnedColumns = new Set<string>();

/** Test-only: lets a spec assert the warning without depending on test order. */
export const resetPinnedWarnings = () => warnedColumns.clear();

const warnMissingWidth = (columnKey: string) => {
  if (process.env.NODE_ENV === 'production') return;
  if (warnedColumns.has(columnKey)) return;
  warnedColumns.add(columnKey);

  // eslint-disable-next-line no-console
  console.warn(
    `[DataTable] Column "${columnKey}" is pinned but has no \`width\`. Pinning needs a ` +
      'fixed width to position the columns beside it, so this column will scroll ' +
      'with the rest of the table. Give it a `width`.'
  );
};

/**
 * Maps every pinned column to its sticky offset.
 *
 * Returns an empty object when nothing is pinned, which lets the render path skip
 * per-cell work entirely on the common unpinned table.
 */
export const computePinnedOffsets = <TData>({
  columns,
  widths,
  hasSelection,
  selectionWidth,
}: ComputePinnedParams<TData>): PinnedOffsets => {
  const widthFor = (column: DataTableColumn<TData>) => widths[column.key] ?? column.width;

  const pinnable = columns.filter((column) => {
    if (!column.pinned) return false;

    if (!widthFor(column)) {
      warnMissingWidth(column.key);
      return false;
    }

    return true;
  });

  if (pinnable.length === 0) return {};

  const offsets: PinnedOffsets = {};

  /*
   * The selection column pins left whenever anything else is pinned. A table
   * wide enough to need pinning is a table whose checkboxes should not scroll
   * away from the rows they act on, and deriving it avoids a second prop that
   * would be set the same way every time.
   */
  let leftOffset = 0;
  if (hasSelection) {
    offsets[SELECTION_COLUMN_KEY] = { side: 'left', offset: 0, isEdge: false };
    leftOffset = selectionWidth;
  }

  // Left pins accumulate in column order, so the leftmost sits at the edge.
  const leftPinned = pinnable.filter((column) => column.pinned === 'left');
  leftPinned.forEach((column, index) => {
    offsets[column.key] = {
      side: 'left',
      offset: leftOffset,
      isEdge: index === leftPinned.length - 1,
    };
    leftOffset += widthFor(column) as number;
  });

  // The selection column only carries the divider when nothing else pins left.
  if (hasSelection && leftPinned.length === 0) {
    offsets[SELECTION_COLUMN_KEY].isEdge = true;
  }

  // Right pins accumulate in reverse, so the rightmost sits at the edge.
  const rightPinned = pinnable.filter((column) => column.pinned === 'right');
  let rightOffset = 0;
  for (let index = rightPinned.length - 1; index >= 0; index -= 1) {
    const column = rightPinned[index];
    offsets[column.key] = {
      side: 'right',
      offset: rightOffset,
      isEdge: index === 0,
    };
    rightOffset += widthFor(column) as number;
  }

  return offsets;
};

/** Memoised `computePinnedOffsets`, for use in the table's render path. */
export const usePinnedColumns = <TData>(params: ComputePinnedParams<TData>): PinnedOffsets =>
  useMemo(() => computePinnedOffsets(params), [
    params.columns,
    params.hasSelection,
    params.selectionWidth,
    params.widths,
  ]);

/** Inline sticky offset for a cell, or `undefined` when the column is not pinned. */
export const pinnedCellStyle = (columnKey: string, offsets: PinnedOffsets) => {
  const pinned = offsets[columnKey];
  if (!pinned) return undefined;

  return pinned.side === 'left' ? { left: pinned.offset } : { right: pinned.offset };
};

/**
 * Class names marking a cell as pinned, and whether it carries the divider.
 *
 * `block` selects the BEM element, because header and body cells need different
 * rules: a pinned header cell is sticky on both axes at once and has to sit above
 * both the sticky header row and the pinned body column.
 */
export const pinnedCellClassNames = (
  columnKey: string,
  offsets: PinnedOffsets,
  block: 'cell' | 'header-cell' = 'cell'
) => {
  const pinned = offsets[columnKey];
  if (!pinned) return undefined;

  return {
    [`ds-data-table__${block}--pinned`]: true,
    [`ds-data-table__${block}--pinned-${pinned.side}`]: true,
    [`ds-data-table__${block}--pinned-edge`]: pinned.isEdge,
  };
};
